// Meta-Pixel Advanced Matching: Name/Telefon aus profiles — höchstens EIN Request pro
// Nutzer-ID je Seitenladung (Perf Welle 5, 17.09.2026).
//
// Vorher holte AuthContext `profiles?select=full_name,phone` bei getSession UND bei jedem
// SIGNED_IN/TOKEN_REFRESHED, useMetaPageView zusätzlich bei jedem Routenwechsel (plus
// auth.getUser als Netzwerk-Request) — beim Dashboard-Start 3× dieselbe Zeile, mitten
// zwischen den eigentlichen Dashboard-Abfragen, und auch ohne Marketing-Consent (dann
// verpuffte das Ergebnis, weil window.fbq fehlt).
//
// Jetzt:
//  - nur mit gespeichertem Marketing-Consent (ohne Consent kein Pixel → kein Request);
//  - erst verzögert (3 s, danach Leerlauf), damit die Dashboard-Abfragen Vorrang haben;
//  - Ergebnis je Nutzer-ID gecacht; Folgeaufrufe bekommen dasselbe Promise.
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { hasStoredMarketingConsent } from "@/hooks/useCookieConsent";

export interface MetaPixelUserData {
  em?: string;
  fn?: string;
  ln?: string;
  ph?: string;
}

const VERZOEGERUNG_MS = 3000;

const cache = new Map<string, Promise<MetaPixelUserData>>();

function nachLeerlauf(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      };
      if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(() => resolve(), { timeout: 1000 });
      else resolve();
    }, VERZOEGERUNG_MS);
  });
}

/**
 * Liefert die Matching-Daten für `user` (verzögert, einmal je Nutzer-ID).
 * `null`, wenn kein Marketing-Consent gespeichert ist — dann wird nichts gecacht, damit
 * ein späterer Aufruf nach erteilter Zustimmung doch noch lädt.
 */
export function ladeMetaPixelUserData(
  user: Pick<User, "id" | "email" | "user_metadata"> | null | undefined,
): Promise<MetaPixelUserData> | null {
  if (!user?.id || typeof window === "undefined") return null;
  const hit = cache.get(user.id);
  if (hit) return hit;
  if (!hasStoredMarketingConsent()) return null;

  const promise = (async (): Promise<MetaPixelUserData> => {
    await nachLeerlauf();
    const meta = user.user_metadata || {};
    // Richer data from profiles table — Fehler egal, dann user_metadata.
    let profilePhone: string | undefined;
    let profileName: string | undefined;
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, phone")
        .eq("user_id", user.id)
        .maybeSingle();
      if (profile) {
        profileName = profile.full_name || undefined;
        profilePhone = profile.phone || undefined;
      }
    } catch {
      // Ignore - fall back to user_metadata
    }
    const fullName = profileName || (meta.full_name as string) || "";
    const nameParts = fullName.trim().split(/\s+/);
    return {
      em: user.email,
      fn: nameParts[0] || undefined,
      ln: nameParts.length > 1 ? nameParts.slice(1).join(" ") : undefined,
      ph: profilePhone || (meta.phone as string) || undefined,
    };
  })();
  cache.set(user.id, promise);
  return promise;
}
